import type { CMSRoomType } from '@/cms/fields/mappers/productDetailPageFieldsMapper';
import {
  Icon,
  Typography,
  tk,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
  Sheet,
  SheetPortal,
  type SheetVariant,
  SheetContentWrapper,
} from '@sunwebgroup/ui-components';
import { useState } from 'react';
import { styled } from 'styled-components';
import { Detail } from './Detail';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics';
import { Gallery } from './Gallery';
import { isEmpty } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints';

const Wrapper = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${tk('spacing_xl')} ${tk('spacing_lg')};
  margin-bottom: ${tk('spacing_xs')};
  box-shadow: ${tk('shadow_md_bottom')};
  border-radius: ${tk('radius_large')};
  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  text-align: left;
`;

const SubLabel = styled(Typography)`
  margin-top: ${tk('spacing_xs')};
  display: flex;
  color: ${tk('color_semantic_text_strong')};
  align-items: center;

  ${Icon} {
    margin-right: ${tk('spacing_2xs')};
  }
`;

const Characteristics = styled.ul`
  padding: ${tk('spacing_lg')} ${tk('spacing_2xl')};

  @media ${tk('mq_tablet')} {
    columns: 2;
  }
`;

interface RoomTypeProps {
  roomType: CMSRoomType;
}

export function RoomType({ roomType }: RoomTypeProps) {
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [clickedItemIndex, setClickedItemIndex] = useState(0);
  const { sendEvent } = useAnalytics();
  const intl = useIntl();
  const { isTablet } = useBreakpoints();
  const sheetWrapperSide = isTablet ? 'right' : ('bottom' as SheetVariant);

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);
  const handleDetailImageClick = (index: number) => openDetail(index);

  const closeDetail = () => {
    setShowDetail(false);
  };

  const openDetail = (index: number) => {
    setClickedItemIndex(index);
    setShowDetail(true);
  };

  const handleOpenModal = () => {
    openModal();
    sendEvent(
      createPDPClickEvent({
        element: 'Element clicked',
        description: 'Roomtype description',
        result: 'Open modal with more details',
        type: roomType.name,
      })
    );
  };

  const handleCloseModal = () => {
    closeModal();
    sendEvent(
      createPDPClickEvent({
        element: 'Element clicked',
        description: 'Roomtype description',
        result: 'Close modal with more details',
        type: roomType.name,
      })
    );
  };

  return (
    <>
      <Wrapper onClick={handleOpenModal}>
        <TitleWrapper>
          <Typography $variant="body-lg-bold">{roomType.name}</Typography>
          <SubLabel $variant="body-sm">
            <Icon glyph="IconTwoPersons" />
            {roomType.capacityText}
          </SubLabel>
        </TitleWrapper>
        {(roomType.characteristics || !isEmpty(roomType.imageUrls)) && <Icon glyph="IconChevronRight" />}
      </Wrapper>
      {(roomType.characteristics || !isEmpty(roomType.imageUrls)) && (
        <>
          <Sheet open={showModal} onOpenChange={handleCloseModal}>
            <SheetPortal>
              <SheetOverlay onClick={() => handleCloseModal} />
              <SheetContent $side={sheetWrapperSide}>
                <SheetHeader>
                  <SheetTitle>{roomType.name}</SheetTitle>
                  <SheetClose>{intl.formatMessage({ id: 'close' })}</SheetClose>
                </SheetHeader>
                <SheetContentWrapper>
                  {roomType.imageUrls?.length > 0 && (
                    <Gallery roomType={roomType} handleDetailImageClick={handleDetailImageClick} />
                  )}
                  {roomType.characteristics && (
                    <Characteristics>
                      {roomType.characteristics.map((characteristic, index) => (
                        <li key={index}>
                          <Typography $variant="body-md">{characteristic}</Typography>
                        </li>
                      ))}
                    </Characteristics>
                  )}
                </SheetContentWrapper>
              </SheetContent>
            </SheetPortal>
          </Sheet>
          {!isEmpty(roomType.imageUrls) && (
            <Detail
              onClose={closeDetail}
              open={showDetail}
              startFromIndex={clickedItemIndex}
              roomtypeLabel={roomType.name}
              roomtypeImages={roomType.imageUrls}
            />
          )}
        </>
      )}
    </>
  );
}
